import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Copy, Brain, MessageSquare, Sparkles, KeyRound, Send, Check, X } from "lucide-react";

/**
 * One-click ChatGPT strategy-review export + auto-tune chat.
 *
 * Two flows:
 *   A. Manual — Download / Copy the JSON bundle, paste into ChatGPT yourself.
 *   B. Auto — hit "Ask ChatGPT to auto-tune" and the backend calls the LLM
 *      directly. Suggested params render with a one-click Apply button.
 *      Multi-turn: type follow-up questions in the chat.
 *
 * Users can plug in their OWN OpenAI API key (paid ChatGPT account) via
 * the Settings card. Otherwise the Emergent Universal Key is used.
 */
export default function ChatGPTExport() {
  const [days, setDays] = useState(30);
  const [signalsLimit, setSignalsLimit] = useState(200);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  // ---- Auto-tune state ----
  const [tuneSettings, setTuneSettings] = useState(null);
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [modelInput, setModelInput] = useState("gpt-5.4");
  const [providerInput, setProviderInput] = useState("openai");
  const [session, setSession] = useState(null); // {session_id, messages:[{role,content}], suggested_params, model, used_user_key}
  const [tuneRunning, setTuneRunning] = useState(false);
  const [followupText, setFollowupText] = useState("");
  const [applying, setApplying] = useState(false);
  const [deltaMode, setDeltaMode] = useState(true);  // enabled by default — saves ~80% tokens
  const chatEndRef = useRef(null);

  const loadTuneSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/chatgpt-tune/settings");
      setTuneSettings(data);
      setModelInput(data.model || "gpt-5.4");
      setProviderInput(data.provider || "openai");
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("tune settings load failed:", e?.response?.status || e?.message);
      }
    }
  }, []);
  useEffect(() => { loadTuneSettings(); }, [loadTuneSettings]);
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [session]);

  const saveTuneSettings = async () => {
    try {
      const body = { model: modelInput, provider: providerInput };
      if (apiKeyInput.trim() || apiKeyInput === "") body.api_key = apiKeyInput;
      const { data } = await api.put("/chatgpt-tune/settings", body);
      setTuneSettings(data);
      setApiKeyInput("");
      setShowKeyInput(false);
      toast.success(data.has_user_key
        ? "Your OpenAI key saved (encrypted). Now using your paid account."
        : "Model/provider saved. Using Emergent Universal Key.");
    } catch (e) {
      toast.error("Failed to save settings");
    }
  };

  const startAutoTune = async () => {
    setTuneRunning(true);
    setSession(null);
    try {
      const { data } = await api.post(
        `/chatgpt-tune?days=${days}&signals_limit=${signalsLimit}`);
      setSession({
        session_id: data.session_id,
        messages: [{ role: "assistant", content: data.response }],
        suggested_params: data.suggested_params,
        model: data.model,
        used_user_key: data.used_user_key,
      });
      const n = data.suggested_params ? Object.keys(data.suggested_params).length : 0;
      toast.success(n ? `${data.model} suggested ${n} params` : `${data.model} responded (no params extracted)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "LLM call failed");
    } finally {
      setTuneRunning(false);
    }
  };

  const sendFollowup = async () => {
    const msg = followupText.trim();
    if (!msg || !session) return;
    setTuneRunning(true);
    setSession((s) => ({
      ...s,
      messages: [...s.messages, { role: "user", content: msg }],
    }));
    setFollowupText("");
    try {
      const { data } = await api.post(
        `/chatgpt-tune/${session.session_id}/followup`,
        { message: msg, delta_mode: deltaMode });
      setSession((s) => ({
        ...s,
        messages: [...s.messages, { role: "assistant", content: data.response,
                                     delta_mode: data.delta_mode,
                                     delta_bytes: data.delta_bytes }],
        suggested_params: data.suggested_params || s.suggested_params,
      }));
      if (data.delta_mode && data.delta_bytes) {
        const kb = Math.round(data.delta_bytes / 1024);
        toast.success(`Δ mode · ${kb} KB shipped (vs ~40 KB full)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Follow-up failed");
    } finally {
      setTuneRunning(false);
    }
  };

  const applySuggested = async () => {
    if (!session?.suggested_params) return;
    setApplying(true);
    try {
      const { data } = await api.post(
        `/chatgpt-tune/${session.session_id}/apply`,
        { params: session.suggested_params });
      const appliedCount = Object.keys(data.params || {}).length;
      const skippedCount = (data.skipped_fields || []).length;
      toast.success(`Applied ${appliedCount} params${skippedCount ? ` (${skippedCount} skipped)` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/chatgpt-export",
        { params: { days, signals_limit: signalsLimit } });
      setPreview(data);
    } catch (e) {
      toast.error("Failed to build export");
    } finally {
      setBusy(false);
    }
  }, [days, signalsLimit]);

  useEffect(() => { load(); }, [load]);

  const download = () => {
    if (!preview) return;
    const blob = new Blob([JSON.stringify(preview, null, 2)],
      { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const a = document.createElement("a");
    a.href = url;
    a.download = `myedge-strategy-review-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Downloaded — paste into ChatGPT with the prompt");
  };

  const copy = async () => {
    if (!preview) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(preview, null, 2));
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Clipboard blocked — use Download instead");
    }
  };

  const copyPrompt = async () => {
    if (!preview?.prompt) return;
    try {
      await navigator.clipboard.writeText(preview.prompt);
      toast.success("Prompt copied — paste before the JSON in ChatGPT");
    } catch {
      toast.error("Clipboard blocked");
    }
  };

  const sizeKb = preview
    ? Math.round(JSON.stringify(preview).length / 1024)
    : 0;

  return (
    <div className="space-y-6" data-testid="chatgpt-export-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <MessageSquare size={20} className="text-emerald-400" />
            <h1 className="text-2xl tracking-tight text-slate-100">
              ChatGPT Strategy Review Export
            </h1>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-3xl uppercase tracking-[0.15em]">
            One-click download of everything an LLM needs to critique &amp;
            re-tune your strategy: config, signals, rejections + shadow P&amp;L,
            trades, ML output, screener, costs, field schema.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Window</label>
          <select
            data-testid="export-days-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-slate-900 border border-slate-700 rounded-sm px-2 py-1 text-xs text-slate-200"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
          <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 ml-2">Signals</label>
          <select
            data-testid="export-signals-select"
            value={signalsLimit}
            onChange={(e) => setSignalsLimit(Number(e.target.value))}
            className="bg-slate-900 border border-slate-700 rounded-sm px-2 py-1 text-xs text-slate-200"
          >
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
          <button
            data-testid="export-reload-btn"
            onClick={load}
            disabled={busy}
            className="px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-[0.15em] border border-slate-700 hover:bg-slate-800/50 disabled:opacity-50"
          >
            Rebuild
          </button>
        </div>
      </div>

      {/* ============ AUTO-TUNE PANEL ============ */}
      <div className="surface rounded-sm p-5 border-l-2 border-l-emerald-500"
           data-testid="auto-tune-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-emerald-400" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">
                Auto-tune
              </div>
            </div>
            <h2 className="text-lg text-slate-100 mt-1">Ask ChatGPT directly</h2>
            <p className="text-[11px] text-slate-500 mt-1 max-w-2xl">
              Runs the full export through {tuneSettings?.model || "gpt-5.4"} and returns
              a suggested params JSON with a one-click Apply. Multi-turn chat
              — keep asking follow-ups.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {tuneSettings && (
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mr-2">
                Using{" "}
                <span className={
                  tuneSettings.source === "user_db" ? "text-emerald-300"
                  : tuneSettings.source === "env_openai" ? "text-emerald-300"
                  : "text-blue-300"
                }>
                  {tuneSettings.source === "user_db" && "your OpenAI key (DB)"}
                  {tuneSettings.source === "env_openai" && "your OpenAI key (env)"}
                  {tuneSettings.source === "emergent" && "Emergent key"}
                  {tuneSettings.source === "none" && "no credentials"}
                </span>
                {" · "}
                <span className="font-mono text-slate-300">{tuneSettings.model}</span>
              </div>
            )}
            <button
              data-testid="tune-settings-btn"
              onClick={() => setShowKeyInput((v) => !v)}
              className="px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-[0.15em] border border-slate-700 hover:bg-slate-800/50 flex items-center gap-1.5"
            >
              <KeyRound size={12} /> Settings
            </button>
            <button
              data-testid="auto-tune-btn"
              onClick={startAutoTune}
              disabled={tuneRunning}
              className="px-4 py-1.5 rounded-sm text-[11px] uppercase tracking-[0.15em] border border-emerald-500 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 flex items-center gap-1.5 disabled:opacity-50"
            >
              <Sparkles size={12} /> {tuneRunning && !session ? "Analysing…" : "Ask ChatGPT"}
            </button>
          </div>
        </div>

        {/* Settings expand */}
        {showKeyInput && (
          <div className="mb-4 p-3 rounded-sm border border-slate-700 bg-slate-900/60"
               data-testid="tune-settings-panel">
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                  OpenAI API Key
                </label>
                <input
                  data-testid="tune-api-key-input"
                  type="password"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder={tuneSettings?.has_user_key ? "•••• saved · type to change" : "sk-proj-..."}
                  className="w-full mt-1 bg-slate-950 border border-slate-700 rounded-sm px-3 py-2 text-xs font-mono text-slate-200 focus:border-emerald-500 outline-none"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  Stored encrypted (Fernet) in Mongo. Leave blank to keep current. Type just a space to clear.
                </div>
                <div className="text-[10px] text-slate-400 mt-2 italic leading-relaxed">
                  Alternative for single-tenant deploys: set{" "}
                  <span className="font-mono text-slate-300">OPENAI_API_KEY=sk-...</span> in{" "}
                  <span className="font-mono text-slate-300">backend/.env</span> (preview) or via the Emergent Deploy → Environment Secrets UI (production). Env var wins if no per-user key is saved above.
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                  Provider
                </label>
                <select
                  data-testid="tune-provider-select"
                  value={providerInput}
                  onChange={(e) => setProviderInput(e.target.value)}
                  className="w-full mt-1 bg-slate-950 border border-slate-700 rounded-sm px-3 py-2 text-xs text-slate-200"
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic (Claude)</option>
                  <option value="gemini">Google Gemini</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                  Model
                </label>
                <input
                  data-testid="tune-model-input"
                  value={modelInput}
                  onChange={(e) => setModelInput(e.target.value)}
                  placeholder="gpt-5.4 / gpt-5.2 / gpt-4o / o3 / claude-sonnet-4-6"
                  className="w-full mt-1 bg-slate-950 border border-slate-700 rounded-sm px-3 py-2 text-xs font-mono text-slate-200 focus:border-emerald-500 outline-none"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  OpenAI: gpt-5.5 / gpt-5.4 / gpt-5.2 / gpt-4o / o3 · Claude: claude-sonnet-4-6 · Gemini: gemini-3.1-pro-preview
                </div>
              </div>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={() => { setShowKeyInput(false); setApiKeyInput(""); }}
                className="px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-[0.15em] border border-slate-700 text-slate-400 hover:bg-slate-800/50"
              >
                Cancel
              </button>
              <button
                data-testid="tune-settings-save-btn"
                onClick={saveTuneSettings}
                className="px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-[0.15em] border border-emerald-500 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
              >
                Save
              </button>
            </div>
          </div>
        )}

        {/* Conversation */}
        {session && (
          <div className="space-y-3" data-testid="tune-conversation">
            {session.messages.map((m, i) => (
              // Chat messages are append-only and keyed by session + position;
              // list order never changes so index-based key is stable here.
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${session.session_id}-${i}`} className={`rounded-sm p-3 ${
                m.role === "user"
                  ? "bg-slate-900/60 border-l-2 border-l-slate-500"
                  : "bg-emerald-500/5 border-l-2 border-l-emerald-500"
              }`}>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2 flex items-center justify-between">
                  <span>{m.role === "user" ? "You" : `Assistant · ${session.model}`}</span>
                  {m.delta_mode && (
                    <span className="text-emerald-400 font-mono normal-case tracking-normal">
                      Δ {m.delta_bytes ? Math.round(m.delta_bytes / 1024) + " KB" : "delta"}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-200 whitespace-pre-wrap leading-relaxed">
                  {m.content}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />

            {/* Suggested params card */}
            {session.suggested_params && (
              <div className="rounded-sm p-4 border-2 border-emerald-500/50 bg-emerald-500/5"
                   data-testid="tune-suggested-params">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">
                      Suggested params
                    </div>
                    <div className="text-xs text-slate-300 mt-1">
                      {Object.keys(session.suggested_params).length} fields ready to apply
                    </div>
                  </div>
                  <button
                    data-testid="tune-apply-btn"
                    onClick={applySuggested}
                    disabled={applying}
                    className="px-4 py-2 rounded-sm text-xs uppercase tracking-[0.15em] bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-2 disabled:opacity-50"
                  >
                    <Check size={14} /> {applying ? "Applying…" : "Apply to strategy"}
                  </button>
                </div>
                <pre className="text-[11px] font-mono text-slate-300 bg-slate-950/60 p-3 rounded-sm overflow-x-auto">
                  {JSON.stringify(session.suggested_params, null, 2)}
                </pre>
              </div>
            )}

            {/* Follow-up input + delta toggle */}
            <div className="pt-2 space-y-2">
              <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-slate-500 cursor-pointer">
                <input
                  data-testid="tune-delta-toggle"
                  type="checkbox"
                  checked={deltaMode}
                  onChange={(e) => setDeltaMode(e.target.checked)}
                  className="accent-emerald-500"
                />
                Δ Delta mode — ship only what changed since last turn (~5 KB vs ~40 KB)
              </label>
              <div className="flex gap-2">
                <input
                  data-testid="tune-followup-input"
                  value={followupText}
                  onChange={(e) => setFollowupText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !tuneRunning) sendFollowup(); }}
                  placeholder="Follow up… e.g. 'What if I doubled the risk?'"
                  disabled={tuneRunning}
                  className="flex-1 bg-slate-950 border border-slate-700 rounded-sm px-3 py-2 text-xs text-slate-200 focus:border-emerald-500 outline-none disabled:opacity-50"
                />
                <button
                  data-testid="tune-followup-btn"
                  onClick={sendFollowup}
                  disabled={tuneRunning || !followupText.trim()}
                  className="px-4 py-2 rounded-sm text-xs uppercase tracking-[0.15em] border border-emerald-500 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Send size={12} /> Send
                </button>
                <button
                  onClick={() => setSession(null)}
                  title="Start a fresh conversation"
                  className="px-3 py-2 rounded-sm text-xs border border-slate-700 text-slate-400 hover:bg-slate-800/50"
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============ MANUAL FLOW (below) ============ */}
      {/* Action row: Download + Copy */}
      <div className="grid md:grid-cols-3 gap-3">
        <ActionCard
          testid="export-download-btn"
          icon={<Download size={18} />}
          title="Download JSON"
          subtitle={`${sizeKb} KB — one file, paste into ChatGPT`}
          onClick={download}
          tone="primary"
        />
        <ActionCard
          testid="export-copy-btn"
          icon={<Copy size={18} />}
          title="Copy JSON"
          subtitle="to clipboard for direct paste"
          onClick={copy}
        />
        <ActionCard
          testid="export-copy-prompt-btn"
          icon={<MessageSquare size={18} />}
          title="Copy analyst prompt"
          subtitle="paste ABOVE the JSON in ChatGPT"
          onClick={copyPrompt}
        />
      </div>

      {/* How to use */}
      <div className="surface rounded-sm p-5" data-testid="export-howto">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
          How to use
        </div>
        <ol className="text-xs text-slate-300 space-y-2 leading-relaxed list-decimal list-inside">
          <li>Click <strong>Copy analyst prompt</strong> — paste it as your first message in ChatGPT (any model, GPT-4o / Claude Sonnet / etc.).</li>
          <li>Click <strong>Download JSON</strong> or <strong>Copy JSON</strong> and paste the JSON block <em>right after</em> the prompt.</li>
          <li>ChatGPT will return a critique + a suggested JSON block of new strategy params.</li>
          <li>Paste that suggested block into <a href="/algorithm" className="text-blue-400 hover:underline">/algorithm → Apply Params</a>. Confirm each field shows up as a green chip in "Last Applied".</li>
          <li>Run 5-10 more paper sessions and repeat if needed.</li>
        </ol>
      </div>

      {/* ML section explainer — the user asked to explain this */}
      <div className="surface rounded-sm p-5" data-testid="export-ml-explainer">
        <div className="flex items-center gap-2 mb-3">
          <Brain size={16} className="text-purple-400" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
            About the ML section — when will it help?
          </div>
        </div>
        <div className="text-xs text-slate-300 leading-relaxed space-y-3">
          <p>
            <strong>What it does:</strong> the ML page trains an XGBoost classifier that predicts
            the probability a signal will exit profitably before its stop is hit. It learns from
            every closed trade you accumulate (currently{" "}
            <span className="font-mono text-purple-300">{preview?.ml?.samples_labeled || 0}</span>{" "}
            labeled samples).
          </p>
          <p>
            <strong>When it helps:</strong> it becomes useful <em>only</em> after ~
            <span className="font-mono text-purple-300">{preview?.ml?.min_samples_for_training || 30}</span>{" "}
            closed trades — below that count the model is overfitted noise. Once you turn on{" "}
            <span className="font-mono text-purple-300">ml_gating_enabled</span> the engine will
            reject any signal whose predicted probability is below{" "}
            <span className="font-mono text-purple-300">ml_gating_threshold</span> (default 0.55) —
            effectively a second-opinion filter on top of the deterministic strategy.
          </p>
          <p>
            <strong>Current status:</strong>{" "}
            {preview?.ml?.model_trained ? (
              <span className="text-pos">model trained (v{preview.ml.model?.version})</span>
            ) : (
              <span className="text-warn">model not trained yet</span>
            )}
            {" · "}
            {preview?.ml?.gate_enabled ? (
              <span className="text-pos">gate ENABLED</span>
            ) : (
              <span className="text-slate-500">gate DISABLED (recommended until you have 100+ trades)</span>
            )}
            {preview?.ml?.model?.val_auc && (
              <> · val AUC{" "}
                <span className="font-mono text-purple-300">
                  {preview.ml.model.val_auc.toFixed(3)}
                </span>
              </>
            )}
          </p>
          <p className="text-slate-400 italic">
            The export bundles all ML metadata (feature importance, AUC, precision/recall) so
            ChatGPT can look at it and tell you whether the model is trustworthy yet.
          </p>
        </div>
      </div>

      {/* What's inside */}
      {preview && (
        <div className="surface rounded-sm p-5" data-testid="export-manifest">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
            Bundle manifest ({sizeKb} KB)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-mono">
            <ManifestItem k="strategy_config" v={preview.strategy_config ? "✓ effective params" : "—"} />
            <ManifestItem k="universe" v={`${(preview.universe?.active_paper || []).length} paper underlyings`} />
            <ManifestItem k="signals" v={`${preview.signals?.length || 0} signals`} />
            <ManifestItem k="performance" v={
              preview.performance?.total_trades
                ? `${preview.performance.total_trades} trades · ${(preview.performance.win_rate * 100).toFixed(1)}% wins`
                : "no closed trades"
            } />
            <ManifestItem k="shadow_rejected" v={`${preview.shadow_rejected?.rows?.length || 0} risk-rejected · ₹${preview.shadow_rejected?.summary?.would_have_made || 0} counterfactual`} />
            <ManifestItem k="candle_summary_last_14d" v={`${preview.candle_summary_last_14d?.length || 0} trading days`} />
            <ManifestItem k="ml" v={preview.ml?.model_trained ? `trained v${preview.ml.model?.version}` : "not trained"} />
            <ManifestItem k="screener_latest" v={preview.screener_latest?.top_10?.length ? `${preview.screener_latest.top_10.length} top-ranked` : "—"} />
            <ManifestItem k="cost_schedule" v={preview.cost_schedule?.NSE ? "NSE + BSE" : "—"} />
            <ManifestItem k="v2_shadow" v={preview.v2_shadow ? "included" : "—"} />
            <ManifestItem k="strategy_field_schema" v={`${Object.keys(preview.strategy_field_schema || {}).length} fields`} />
          </div>
        </div>
      )}

      {/* JSON preview */}
      {preview && (
        <div className="surface rounded-sm overflow-hidden" data-testid="export-preview">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Preview (first 4KB)
            </div>
            <div className="text-[10px] text-slate-500 font-mono">{sizeKb} KB total</div>
          </div>
          <pre className="text-[10px] font-mono text-slate-300 p-4 overflow-auto max-h-96 bg-slate-950/50">
            {JSON.stringify(preview, null, 2).slice(0, 4000)}
            {sizeKb > 4 && "\n\n… (truncated · full contents in Download / Copy)"}
          </pre>
        </div>
      )}
    </div>
  );
}

function ActionCard({ icon, title, subtitle, onClick, tone, testid }) {
  const primary = tone === "primary";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`surface rounded-sm p-4 text-left hover:border-blue-500 transition-colors ${
        primary ? "border-blue-500 bg-blue-500/5" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <span className={primary ? "text-blue-300" : "text-slate-400"}>{icon}</span>
        <div>
          <div className={`text-sm ${primary ? "text-blue-200" : "text-slate-100"}`}>{title}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-[0.15em] mt-0.5">{subtitle}</div>
        </div>
      </div>
    </button>
  );
}

function ManifestItem({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border border-slate-800 rounded-sm px-3 py-1.5">
      <span className="text-slate-500 truncate">{k}</span>
      <span className="text-slate-300 shrink-0">{v}</span>
    </div>
  );
}
